import React from "react"
import Popup from "../../component/popup/popup"
import { AgentData } from "../../models"
import IconsColor from "../../util/iconsColor"
import Table, { TableColumn } from "../../component/table/table"
import CustomizedMenu from "component/muiMenu/muiMenu"
import utilStyles from "../../asset/styles/util/util.module.scss"
import { toTitleCase } from "util/textcase"
import viewStyles from "../../asset/styles/agents/agentViewPopup.module.scss"

export type AgencyPopupMode = "add" | "edit"

type AgentViewPopupProps = {
    isOpen: boolean
    onClose: () => void
    mode: AgencyPopupMode
    agentsData: AgentData[]
    agencyName?: string
    onAddAgentClick?: () => void
    onEditAgent?: (agent: AgentData) => void
    onStatusChange?: (agent: AgentData) => void
    onDeleteAgent?: (agent: AgentData) => void
}

const AgentViewPopup: React.FC<AgentViewPopupProps> = ({
    isOpen,
    onClose,
    agentsData,
    agencyName,
    onAddAgentClick,
    onEditAgent,
    onStatusChange,
    onDeleteAgent,
}) => {
    const resolvedAgencyName = agencyName ?? agentsData[0]?.agency_name ?? ""
    const title = `Agents - ${resolvedAgencyName}`

    const columns: TableColumn<AgentData>[] = [
        {
            key: "agent_name",
            label: "Agent Name",
            width: "22%",
            align: "left",
            render: (value: string) => <span className={viewStyles.agentName}>{toTitleCase(value)}</span>,
        },
        {
            key: "office_name",
            label: "Office",
            width: "18%",
            align: "left",
            render: (value: string) => <span className={viewStyles.officeName}>{toTitleCase(value)}</span>,
        },
        {
            key: "contact_number",
            label: "Contact Info",
            width: "28%",
            align: "left",
            render: (value: string, row: AgentData) => (
                <div>
                    <div className={viewStyles.contactPhone}>{value || "-"}</div>
                    {row.email ? (
                        <div className={viewStyles.contactEmail}>{row.email}</div>
                    ) : (
                        <div className={viewStyles.contactEmailEmpty}>-</div>
                    )}
                </div>
            ),
        },
        {
            key: "flag_active",
            label: "Status",
            width: "12%",
            align: "left",
            render: (value: boolean) => (
                <span className={value ? utilStyles.statusBadgeActive : utilStyles.statusBadgeInactive}>
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "left",
            sortable: false,
            render: (_: unknown, row: AgentData) => {
                // First agent from AgentListAPI is the agency contact and cannot be deleted.
                const isPrimaryAgent = row.agent_id === agentsData[0]?.agent_id

                return (
                    <CustomizedMenu
                        buttonText=""
                        menuItems={[
                            {
                                label: "Edit",
                                onClick: () => onEditAgent?.(row),
                            },
                            {
                                label: `Set Status to ${row.flag_active ? "Inactive" : "Active"}`,
                                onClick: () => onStatusChange?.(row),
                            },
                            {
                                label: "Delete",
                                onClick: () => onDeleteAgent?.(row),
                                disabled: isPrimaryAgent,
                                style: { color: isPrimaryAgent ? undefined : "#d32f2f" },
                            },
                        ]}
                        buttonVariant="text"
                        buttonStyle={{ padding: "0px", minWidth: "0px" }}
                        buttonEndIcon={
                            <IconsColor
                                iconName="moreVert"
                                width="20"
                                height="20"
                                fillColor="none"
                                strokeColor="#7a7a7a"
                            />
                        }
                    />
                )
            },
        },
    ]

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            primaryButton={{
                label: "Add Agent",
                onClick: () => onAddAgentClick?.()
            }}
            size="large"
            heightFiexd={true}
        >
            <div style={{ paddingTop: 5 }}>
                <div className={viewStyles.agentsToolbar}>
                    <p className={viewStyles.agentsCount}>
                        <strong>{agentsData.length}</strong> Agents
                    </p>
                </div>

                <Table
                    columns={columns}
                    data={agentsData}
                    keyExtractor={(row: AgentData) => row.agent_id}
                    emptyMessage="No agents available"
                    minWidth={800}
                    showHeader={true}
                    maxHeight={500}
                />
            </div>
        </Popup>
    )
}

export default AgentViewPopup
